import React, { useEffect, useMemo, useState, useCallback } from "react";
import styled from "styled-components";
import {
  Card,
  Button,
  Row,
  Col,
  Modal,
  Container,
  Form,
} from "react-bootstrap";
import { useSelector, useDispatch } from "react-redux";
import { OrangeButton, CloseIcon } from "../components/DefaultImports.js";
import {
  createTables,
  fetchTables,
  createPlaceOfTable,
  fetchPlaceOfTable,
  bulkUpdateTables,
  fetchTableConnections,
  updateTableConnections,
} from "../actions/restaurantActions";
import { api } from "../App";

/* ---------- styles ---------- */

const StyledCard = styled(Card)`
  margin: 10px 0;
  box-shadow: 0px 4px 8px 0px rgba(0, 0, 0, 0.2);
  border-radius: 0 0 5px 5px;
  overflow: hidden;
`;

const CardTop = styled.div`
  padding: 10px;
  background-color: #faf6e9;
  position: relative;
`;

const CardBodyBox = styled.div`
  background: white;
  padding: 14px;
`;

const StyledSelect = styled.select`
  width: 100%;
  height: 2rem;
  font-size: 1rem;
  border: none;
  border-radius: 4px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 10px;
  justify-content: flex-end;
  flex-wrap: wrap;
`;

const RoomTitle = styled.div`
  font-size: 1rem;
  font-weight: 700;
  color: #433034;
`;

const RoomSubtitle = styled.div`
  font-size: 0.9rem;
  color: #777;
  margin-top: 2px;
`;

const EmptyState = styled.div`
  border: 1px dashed #d9d9d9;
  border-radius: 8px;
  padding: 20px;
  text-align: center;
  color: #777;
  background: #fff;
`;

const TableListScroll = styled.div`
  max-height: clamp(260px, 55vh, 600px);
  overflow-y: auto;
  overflow-x: hidden;
  padding-right: 4px;

  &::-webkit-scrollbar {
    width: 10px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 8px;
  }

  &::-webkit-scrollbar-thumb {
    background: #b8b8b8;
    border-radius: 8px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: #999;
  }

  scrollbar-width: thin;
  scrollbar-color: #b8b8b8 #f1f1f1;
`;

const TableList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const TableRowCard = styled.div`
  border: 1px solid #ececec;
  border-radius: 8px;
  padding: 12px;
  background: #fff;
`;

const TableRowGrid = styled.div`
  display: grid;
  grid-template-columns: 120px minmax(140px, 1fr) 120px 50px;
  gap: 12px;
  align-items: center;

  @media (max-width: 768px) {
    grid-template-columns: 1fr 1fr;
    gap: 10px;
  }

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

const DeleteCell = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;

  @media (max-width: 768px) {
    justify-content: flex-start;
  }
`;

const SmallInfo = styled.div`
  font-size: 0.85rem;
  color: #666;
`;

const AddFilterBox = styled.button`
  width: 100%;
  padding: 12px 14px;
  border: 1px dashed #99b0ad;
  border-radius: 8px;
  background: #ffffff;
  color: #99b0ad;
  font-weight: 700;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transition: all 0.15s ease;

  &:hover {
    border-color: #ee754c;
    color: #ee754c;
  }
`;

const BulkTableScroll = styled.div`
  width: 100%;
  overflow-x: auto;
  overflow-y: hidden;
`;

/* ---------- component ---------- */

const TablesWithoutDragging = ({ all_tables = [] }) => {
  const dispatch = useDispatch();

  const restaurantZones = useSelector((state) => state.restaurantZones);
  const { zones = [] } = restaurantZones;

  const restaurantDetails = useSelector((state) => state.restaurantDetails);
  const { restaurants } = restaurantDetails;

  const tableConnectionsState = useSelector((state) => state.tableConnections);
  const { connections: tableConnections = [] } = tableConnectionsState;

  const [tables, setTables] = useState([]);
  const [selectedPlace, setSelectedPlace] = useState("");

  /* modals */
  const [showCreateTables, setShowCreateTables] = useState(false);
  const [showDeleteTables, setShowDeleteTables] = useState(false);
  const [showCreateZone, setShowCreateZone] = useState(false);
  const [showDeleteZone, setShowDeleteZone] = useState(false);
  const [showBulkEditor, setShowBulkEditor] = useState(false);

  /* create tables */
  const [numTablesToAdd, setNumTablesToAdd] = useState("");
  const [numSeatsPerTable, setNumSeatsPerTable] = useState("");
  const [isCreating, setIsCreating] = useState(false);

  /* delete tables */
  const [numTablesToDelete, setNumTablesToDelete] = useState("");
  const [isDeleting, setIsDeleting] = useState(false);

  /* create zone */
  const [zoneName, setZoneName] = useState("");
  const [isCreatingZone, setIsCreatingZone] = useState(false);

  /* delete zone */
  const [isDeletingZone, setIsDeletingZone] = useState(false);

  /* bulk editor */
  const [bulkEditPlace, setBulkEditPlace] = useState(null);
  const [bulkRows, setBulkRows] = useState([]);
  const [isBulkEditorSaving, setIsBulkEditorSaving] = useState(false);

  /* table connections */
  const [currentConnectionTableId, setCurrentConnectionTableId] =
    useState(null);
  const [selectedConnectionIds, setSelectedConnectionIds] = useState([]);
  const [isSavingConnections, setIsSavingConnections] = useState(false);

  /* single delete */
  const [deletingRowId, setDeletingRowId] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState({
    show: false,
    tableId: null,
    label: "",
  });

  useEffect(() => {
    setTables(all_tables || []);
  }, [all_tables]);

  useEffect(() => {
    if (zones.length > 0 && !selectedPlace) {
      setSelectedPlace(zones[0].id);
    }
  }, [zones, selectedPlace]);

  useEffect(() => {
    if (restaurants?.id) {
      dispatch(fetchTableConnections(restaurants.id));
    }
  }, [dispatch, restaurants?.id]);

  const selectedZone = useMemo(
    () => zones.find((z) => z.id === selectedPlace),
    [zones, selectedPlace],
  );

  const tablesInSelectedZone = useMemo(() => {
    return (tables || [])
      .filter((t) => t.place_of_table === selectedPlace)
      .sort((a, b) => Number(a.table_number) - Number(b.table_number));
  }, [tables, selectedPlace]);

  const currentConnectionTable = useMemo(() => {
    if (!currentConnectionTableId) return null;
    return tables.find((t) => t.id === currentConnectionTableId) || null;
  }, [tables, currentConnectionTableId]);

  const canCreateTables =
    !!restaurants?.id &&
    !!selectedPlace &&
    Number(numTablesToAdd) > 0 &&
    Number(numSeatsPerTable) > 0;

  const getConnectedTableIds = useCallback(
    (tableId) => {
      if (!tableId || !Array.isArray(tableConnections)) {
        return [];
      }

      return tableConnections
        .filter((connection) => {
          return (
            connection.is_active &&
            (connection.table_a === tableId || connection.table_b === tableId)
          );
        })
        .map((connection) => {
          return connection.table_a === tableId
            ? connection.table_b
            : connection.table_a;
        });
    },
    [tableConnections],
  );

  const startEditingConnections = (tableId) => {
    setCurrentConnectionTableId(tableId);
    setSelectedConnectionIds(getConnectedTableIds(tableId));
  };

  const toggleConnectionTable = (tableId) => {
    setSelectedConnectionIds((prev) => {
      if (prev.includes(tableId)) {
        return prev.filter((id) => id !== tableId);
      }

      return [...prev, tableId];
    });
  };

  const handleSaveConnections = async () => {
    if (!restaurants?.id || !currentConnectionTableId) return;

    try {
      setIsSavingConnections(true);

      await dispatch(
        updateTableConnections(
          restaurants.id,
          currentConnectionTableId,
          selectedConnectionIds,
        ),
      );

      await dispatch(fetchTableConnections(restaurants.id));

      console.log("Connections saved:", {
        tableId: currentConnectionTableId,
        connectedTableIds: selectedConnectionIds,
      });
    } catch (e) {
      console.error("Failed to save table connections:", e);
    } finally {
      setIsSavingConnections(false);
    }
  };

  /* ---------- handlers ---------- */

  const handleCreateTables = async () => {
    if (!canCreateTables) return;

    try {
      setIsCreating(true);

      const payload = {
        place_of_table: selectedPlace,
        num_tables_to_create: Number(numTablesToAdd),
        num_seats: Number(numSeatsPerTable),
      };

      await dispatch(createTables(restaurants.id, payload));
      await dispatch(fetchTables(restaurants.id));

      setShowCreateTables(false);
      setNumTablesToAdd("");
      setNumSeatsPerTable("");
    } catch (e) {
      console.error("Failed to create tables:", e);
    } finally {
      setIsCreating(false);
    }
  };

  const handleDeleteTables = async () => {
    const qty = Number(numTablesToDelete);
    if (!restaurants?.id || !selectedPlace || !qty || qty < 1) return;

    try {
      setIsDeleting(true);
      await api.delete(
        `restaurants/${restaurants.id}/zones/${selectedPlace}/delete-tables/${qty}/`,
      );
      await dispatch(fetchTables(restaurants.id));
      await dispatch(fetchTableConnections(restaurants.id));
      setShowDeleteTables(false);
      setNumTablesToDelete("");
    } catch (e) {
      console.error("Failed to delete tables:", e);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleCreateZone = async () => {
    if (!restaurants?.id || !zoneName.trim()) return;

    try {
      setIsCreatingZone(true);

      const payload = {
        restaurant: restaurants.id,
        place_of_table: zoneName.trim(),
      };

      await dispatch(createPlaceOfTable(restaurants.id, payload));
      await dispatch(fetchPlaceOfTable(restaurants.id));
      setShowCreateZone(false);
      setZoneName("");
    } catch (e) {
      console.error("Failed to create zone:", e);
    } finally {
      setIsCreatingZone(false);
    }
  };

  const handleDeleteZone = async () => {
    if (!restaurants?.id || !selectedPlace) return;

    try {
      setIsDeletingZone(true);

      await api.delete(
        `restaurants/${restaurants.id}/placeoftable/${selectedPlace}/`,
      );

      setShowDeleteZone(false);
      setSelectedPlace("");
      setCurrentConnectionTableId(null);
      setSelectedConnectionIds([]);

      await dispatch(fetchPlaceOfTable(restaurants.id));
      await dispatch(fetchTables(restaurants.id));
      await dispatch(fetchTableConnections(restaurants.id));
    } catch (e) {
      console.error("Failed to delete zone:", e);
    } finally {
      setIsDeletingZone(false);
    }
  };

  const openBulkEditor = () => {
    const initialPlace = selectedPlace || zones[0]?.id || null;
    setBulkEditPlace(initialPlace);

    const rows = (tables || [])
      .filter((t) => t.place_of_table === initialPlace)
      .sort((a, b) => Number(a.table_number) - Number(b.table_number));

    setBulkRows(rows.map((r) => ({ ...r })));
    setCurrentConnectionTableId(null);
    setSelectedConnectionIds([]);
    setShowBulkEditor(true);
  };

  const changeBulkRoom = (placeId) => {
    setBulkEditPlace(placeId);

    const rows = (tables || [])
      .filter((t) => t.place_of_table === placeId)
      .sort((a, b) => Number(a.table_number) - Number(b.table_number));

    setBulkRows(rows.map((r) => ({ ...r })));
    setCurrentConnectionTableId(null);
    setSelectedConnectionIds([]);
  };

  const onBulkRowChange = (id, field, value) => {
    setBulkRows((prev) =>
      prev.map((r) => (r.id === id ? { ...r, [field]: value } : r)),
    );
  };

  const saveBulkEditor = async () => {
    if (!restaurants?.id) return;

    try {
      setIsBulkEditorSaving(true);

      const updates = bulkRows.map((r) => {
        const u = { id: r.id };
        u.table_name = r.table_name ?? "";
        u.number_of_seats = Number(r.number_of_seats) || 0;
        u.round_or_square = r.round_or_square || "square";
        return u;
      });

      await dispatch(bulkUpdateTables(restaurants.id, updates));
      await dispatch(fetchTables(restaurants.id));
      await dispatch(fetchTableConnections(restaurants.id));

      setShowBulkEditor(false);
    } catch (e) {
      console.error("Bulk editor save failed:", e);
    } finally {
      setIsBulkEditorSaving(false);
    }
  };

  const handleDeleteSingleTable = async (tableId) => {
    if (!restaurants?.id || !tableId) return;

    try {
      setDeletingRowId(tableId);
      await api.delete(`restaurants/${restaurants.id}/tables/${tableId}/`);
      await dispatch(fetchTables(restaurants.id));
      await dispatch(fetchTableConnections(restaurants.id));
    } catch (e) {
      console.error("Single table delete failed:", e);
    } finally {
      setDeletingRowId(null);
    }
  };

  return (
    <Container className="no-select">
      <StyledCard>
        <CardTop>
          <Row className="align-items-center">
            <Col md={5}>
              <RoomTitle>Mesas por sala</RoomTitle>
              <RoomSubtitle>
                Seleciona uma sala para ver e gerir as mesas
              </RoomSubtitle>
            </Col>

            <Col md={7}>
              <div
                style={{
                  display: "flex",
                  gap: 8,
                  alignItems: "center",
                  justifyContent: "flex-end",
                  flexWrap: "wrap",
                }}
              >
                <div
                  style={{ minWidth: 220, flex: "1 1 220px", maxWidth: 320 }}
                >
                  <StyledSelect
                    value={selectedPlace || ""}
                    onChange={(e) =>
                      setSelectedPlace(parseInt(e.target.value, 10))
                    }
                  >
                    {zones.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.place_of_table}
                      </option>
                    ))}
                  </StyledSelect>
                </div>

                <OrangeButton
                  size="sm"
                  onClick={() => setShowCreateZone(true)}
                  disabled={!restaurants?.id}
                >
                  Nova sala
                </OrangeButton>

                <Button
                  variant="danger"
                  size="sm"
                  onClick={() => setShowDeleteZone(true)}
                  disabled={!selectedPlace}
                >
                  Eliminar sala
                </Button>
              </div>
            </Col>
          </Row>
        </CardTop>

        <CardBodyBox>
          {selectedZone ? (
            <>
              <div style={{ marginBottom: 14 }}>
                <RoomTitle>{selectedZone.place_of_table}</RoomTitle>
                <RoomSubtitle>
                  {(tablesInSelectedZone || []).length} mesa(s) nesta sala
                </RoomSubtitle>
              </div>

              <div style={{ marginBottom: 16 }}>
                <ActionButtons style={{ justifyContent: "flex-start" }}>
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={openBulkEditor}
                    disabled={!selectedPlace}
                  >
                    Editar lista
                  </Button>

                  <OrangeButton
                    size="sm"
                    onClick={() => setShowCreateTables(true)}
                    disabled={!selectedPlace}
                  >
                    Adicionar mesas
                  </OrangeButton>

                  <Button
                    variant="danger"
                    size="sm"
                    onClick={() => setShowDeleteTables(true)}
                    disabled={!tablesInSelectedZone.length}
                  >
                    Eliminar mesas
                  </Button>
                </ActionButtons>
              </div>

              {tablesInSelectedZone.length === 0 ? (
                <EmptyState>Sem mesas nesta sala.</EmptyState>
              ) : (
                <TableListScroll>
                  <TableList>
                    {tablesInSelectedZone.map((table) => (
                      <TableRowCard key={table.id}>
                        <TableRowGrid>
                          <div>
                            <div style={{ fontWeight: 700 }}>
                              Mesa {table.table_number || "-"}
                            </div>
                            <SmallInfo>ID: {table.id}</SmallInfo>
                          </div>

                          <div>
                            <div style={{ fontWeight: 600 }}>
                              {table.table_name || "Sem nome"}
                            </div>
                            <SmallInfo>
                              {table.round_or_square === "round"
                                ? "Redonda"
                                : "Quadrada"}
                            </SmallInfo>
                          </div>

                          <div>
                            <div>{table.number_of_seats || 0} lugares</div>
                          </div>

                          <DeleteCell>
                            <CloseIcon
                              style={{
                                color: "#99B0AD",
                                right: "0",
                                position: "relative",
                                cursor: "pointer",
                              }}
                              onClick={() =>
                                setConfirmDelete({
                                  show: true,
                                  tableId: table.id,
                                  label:
                                    table.table_number ||
                                    table.table_name ||
                                    `#${table.id}`,
                                })
                              }
                            />
                          </DeleteCell>
                        </TableRowGrid>
                      </TableRowCard>
                    ))}
                  </TableList>
                </TableListScroll>
              )}
            </>
          ) : (
            <EmptyState>
              Ainda não existem salas. Cria uma sala para começar.
            </EmptyState>
          )}
        </CardBodyBox>
      </StyledCard>

      {/* ---- Create Tables ---- */}
      <Modal
        show={showCreateTables}
        onHide={() => setShowCreateTables(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Adicionar mesas</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Número de mesas a adicionar</Form.Label>
              <Form.Control
                type="number"
                min={1}
                value={numTablesToAdd}
                onChange={(e) => setNumTablesToAdd(e.target.value)}
                placeholder="Ex.: 6"
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Número de lugares por mesa</Form.Label>
              <Form.Control
                type="number"
                min={1}
                value={numSeatsPerTable}
                onChange={(e) => setNumSeatsPerTable(e.target.value)}
                placeholder="Ex.: 4"
              />
            </Form.Group>

            <div style={{ fontSize: "0.85rem", color: "#666" }}>
              Posteriormente podes alterar o número de lugares de cada mesa em
              "Editar lista".
            </div>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowCreateTables(false)}
            disabled={isCreating}
          >
            Cancelar
          </Button>
          <OrangeButton
            onClick={handleCreateTables}
            disabled={!canCreateTables || isCreating}
          >
            {isCreating ? "A criar…" : "Guardar"}
          </OrangeButton>
        </Modal.Footer>
      </Modal>

      {/* ---- Delete Tables ---- */}
      <Modal
        show={showDeleteTables}
        onHide={() => setShowDeleteTables(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Eliminar mesas</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="mb-2" style={{ fontSize: "0.9rem", color: "#555" }}>
            Sala selecionada: <b>{selectedZone?.place_of_table || "-"}</b>
            <br />
            Mesas actuais nesta sala: <b>{tablesInSelectedZone.length}</b>
          </div>

          <Form.Group className="mb-3">
            <Form.Label>Número de mesas a eliminar</Form.Label>
            <Form.Control
              type="number"
              min={1}
              value={numTablesToDelete}
              onChange={(e) => setNumTablesToDelete(e.target.value)}
              placeholder="Ex.: 1"
            />
            <Form.Text muted>
              Serão removidas as últimas N mesas da sala.
            </Form.Text>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowDeleteTables(false)}
            disabled={isDeleting}
          >
            Cancelar
          </Button>
          <Button
            variant="danger"
            onClick={handleDeleteTables}
            disabled={
              isDeleting ||
              !restaurants?.id ||
              !selectedPlace ||
              !Number(numTablesToDelete) ||
              Number(numTablesToDelete) < 1
            }
          >
            {isDeleting ? "A eliminar…" : "Eliminar"}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ---- Create Zone ---- */}
      <Modal
        show={showCreateZone}
        onHide={() => setShowCreateZone(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Nova sala</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group>
            <Form.Label>Nome da sala</Form.Label>
            <Form.Control
              type="text"
              value={zoneName}
              onChange={(e) => setZoneName(e.target.value)}
              placeholder="Ex.: Sala Principal"
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowCreateZone(false)}
            disabled={isCreatingZone}
          >
            Cancelar
          </Button>
          <OrangeButton
            onClick={handleCreateZone}
            disabled={isCreatingZone || !zoneName.trim()}
          >
            {isCreatingZone ? "A criar…" : "Guardar"}
          </OrangeButton>
        </Modal.Footer>
      </Modal>

      {/* ---- Delete Zone ---- */}
      <Modal
        show={showDeleteZone}
        onHide={() => setShowDeleteZone(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Eliminar sala</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Tem a certeza que pretende eliminar a sala{" "}
          <b>{selectedZone?.place_of_table || "-"}</b>?
          <br />
          Mesas nesta sala: <b>{tablesInSelectedZone.length}</b>
          <div style={{ fontSize: "0.85rem", color: "#666", marginTop: 8 }}>
            Esta ação não pode ser desfeita.
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowDeleteZone(false)}
            disabled={isDeletingZone}
          >
            Cancelar
          </Button>
          <Button
            variant="danger"
            onClick={handleDeleteZone}
            disabled={isDeletingZone || !selectedPlace}
          >
            {isDeletingZone ? "A eliminar…" : "Eliminar"}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ---- Bulk Edit List ---- */}
      <Modal
        show={showBulkEditor}
        onHide={() => setShowBulkEditor(false)}
        size="lg"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Editar lista de mesas</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <div style={{ marginBottom: 12 }}>
            <Form.Group style={{ margin: 0, maxWidth: 320 }}>
              <Form.Label>Sala</Form.Label>
              <Form.Select
                value={bulkEditPlace || ""}
                onChange={(e) => changeBulkRoom(parseInt(e.target.value, 10))}
              >
                {zones.map((z) => (
                  <option key={z.id} value={z.id}>
                    {z.place_of_table}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </div>

          {currentConnectionTable && (
            <div
              style={{
                marginBottom: 12,
                padding: "10px 12px",
                borderRadius: 8,
                background: "#faf6e9",
                border: "1px solid #eee",
              }}
            >
              <div style={{ fontWeight: 700 }}>
                A configurar junções da mesa{" "}
                {currentConnectionTable.table_name ||
                  currentConnectionTable.table_number}
              </div>
              <div style={{ fontSize: "0.85rem", color: "#666" }}>
                Marca as mesas que podem ser fisicamente juntas a esta mesa.
              </div>
            </div>
          )}

          <BulkTableScroll>
            <div
              style={{
                display: "grid",
                gridTemplateColumns:
                  "minmax(60px, 90px) 1fr 130px 130px 160px 90px",
                gap: 10,
                fontWeight: 700,
                padding: "6px 8px",
                borderBottom: "1px solid #e9ecef",
                background: "#fafafa",
                borderTopLeftRadius: 6,
                borderTopRightRadius: 6,
                minWidth: 850,
              }}
            >
              <div>Mesa</div>
              <div>Nome da mesa</div>
              <div>Lugares</div>
              <div>Formato</div>
              <div>Juntar mesas</div>
              <div>Eliminar</div>
            </div>

            {bulkRows.length === 0 ? (
              <div
                style={{
                  color: "#666",
                  padding: "10px 8px",
                  border: "1px solid #eee",
                  borderTop: "none",
                  borderRadius: "0 0 6px 6px",
                }}
              >
                Sem mesas nesta sala.
              </div>
            ) : (
              <div
                style={{
                  maxHeight: 420,
                  overflowY: "auto",
                  border: "1px solid #eee",
                  borderTop: "none",
                  borderRadius: "0 0 6px 6px",
                  padding: 8,
                  minWidth: 850,
                }}
              >
                {bulkRows.map((row) => (
                  <div
                    key={row.id}
                    style={{
                      display: "grid",
                      gridTemplateColumns:
                        "minmax(60px, 90px) 1fr 130px 130px 160px 90px",
                      gap: 10,
                      alignItems: "center",
                      padding: "6px 8px",
                      borderBottom: "1px solid #f0f0f0",
                      minWidth: 850,
                    }}
                  >
                    <div style={{ fontWeight: 600 }}>
                      {row.table_number || "-"}
                    </div>

                    <Form.Control
                      type="text"
                      value={row.table_name || ""}
                      onChange={(e) =>
                        onBulkRowChange(row.id, "table_name", e.target.value)
                      }
                      placeholder="Nome da mesa"
                    />

                    <Form.Control
                      type="number"
                      min={1}
                      value={row.number_of_seats ?? ""}
                      onChange={(e) =>
                        onBulkRowChange(
                          row.id,
                          "number_of_seats",
                          e.target.value,
                        )
                      }
                      placeholder="Lugares"
                    />

                    <Form.Select
                      value={row.round_or_square || "square"}
                      onChange={(e) =>
                        onBulkRowChange(
                          row.id,
                          "round_or_square",
                          e.target.value,
                        )
                      }
                    >
                      <option value="square">Quadrada</option>
                      <option value="round">Redonda</option>
                    </Form.Select>

                    <div style={{ fontSize: "0.85rem" }}>
                      {currentConnectionTableId === row.id ? (
                        <span style={{ color: "#999", fontWeight: 600 }}>
                          Mesa atual
                        </span>
                      ) : currentConnectionTableId ? (
                        <>
                          <Form.Check
                            type="checkbox"
                            label="Pode juntar"
                            checked={selectedConnectionIds.includes(row.id)}
                            onChange={() => toggleConnectionTable(row.id)}
                          />

                          <Button
                            variant="link"
                            size="sm"
                            style={{
                              padding: 0,
                              display: "block",
                              fontSize: "0.75rem",
                            }}
                            onClick={() => startEditingConnections(row.id)}
                          >
                            editar esta
                          </Button>
                        </>
                      ) : (
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          onClick={() => startEditingConnections(row.id)}
                        >
                          Configurar
                        </Button>
                      )}
                    </div>

                    <div>
                      <CloseIcon
                        style={{
                          color: "#99B0AD",
                          right: "0",
                          position: "relative",
                          cursor: "pointer",
                        }}
                        onClick={() =>
                          setConfirmDelete({
                            show: true,
                            tableId: row.id,
                            label:
                              row.table_number ||
                              row.table_name ||
                              `#${row.id}`,
                          })
                        }
                      />
                    </div>
                  </div>
                ))}

                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    paddingTop: 10,
                  }}
                >
                  <AddFilterBox
                    type="button"
                    onClick={() => setShowCreateTables(true)}
                  >
                    <span style={{ fontSize: 18, lineHeight: "18px" }}>＋</span>
                    <span>Adicionar mesas</span>
                  </AddFilterBox>
                </div>
              </div>
            )}
          </BulkTableScroll>
        </Modal.Body>

        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowBulkEditor(false)}
            disabled={isBulkEditorSaving || isSavingConnections}
          >
            Cancelar
          </Button>

          <Button
            variant="outline-secondary"
            onClick={handleSaveConnections}
            disabled={
              isSavingConnections ||
              !currentConnectionTableId ||
              !restaurants?.id
            }
          >
            {isSavingConnections ? "A guardar junções…" : "Guardar junções"}
          </Button>

          <OrangeButton
            onClick={saveBulkEditor}
            disabled={isBulkEditorSaving || !bulkRows.length}
          >
            {isBulkEditorSaving ? "A guardar…" : "Guardar alterações"}
          </OrangeButton>
        </Modal.Footer>
      </Modal>

      {/* ---- Confirm single delete ---- */}
      <Modal
        show={confirmDelete.show}
        onHide={() =>
          setConfirmDelete({ show: false, tableId: null, label: "" })
        }
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Eliminar mesa</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Tens a certeza que pretendes eliminar esta mesa?
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() =>
              setConfirmDelete({ show: false, tableId: null, label: "" })
            }
            disabled={deletingRowId === confirmDelete.tableId}
          >
            Cancelar
          </Button>
          <Button
            variant="danger"
            onClick={async () => {
              if (!confirmDelete.tableId) return;
              await handleDeleteSingleTable(confirmDelete.tableId);
              setConfirmDelete({ show: false, tableId: null, label: "" });
            }}
            disabled={deletingRowId === confirmDelete.tableId}
          >
            {deletingRowId === confirmDelete.tableId
              ? "A eliminar…"
              : "Eliminar"}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default TablesWithoutDragging;
